import React, { useEffect, useState, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

function TimerBar({ startTime, timeLimit, active, color = 'bg-yellow-400' }) {
  const [pct, setPct] = useState(0);
  const [remaining, setRemaining] = useState(timeLimit || 0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!active || !startTime || !timeLimit) {
      setPct(0);
      setRemaining(timeLimit || 0);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const rem = Math.max(0, Math.ceil(timeLimit - elapsed));
      const p = Math.min(elapsed / timeLimit, 1);
      setRemaining(rem);
      setPct(p);
      if (p >= 1) clearInterval(intervalRef.current);
    };
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [active, startTime, timeLimit]);

  return (
    <div className="w-full mt-2 mb-1">
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
        <div
          className={`h-full ${color} transition-none`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      <div className="text-xs text-right text-gray-500 dark:text-gray-400 mt-0.5">{remaining}s left</div>
    </div>
  );
}

export default function NeverHaveIEverGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });
  const [myAnswer, setMyAnswer] = useState(null); // 'have' | 'havent' | null

  const phase = gameState ? gameState.phase : 'responding';
  const players = gameState ? (gameState.players || []) : [];
  const scores = gameState ? (gameState.scores || {}) : {};
  const round = gameState ? (gameState.round || 1) : 1;
  const rounds = gameState ? (gameState.rounds || 10) : 10;
  const currentStatement = gameState ? gameState.currentStatement : null;
  const responses = gameState ? (gameState.responses || {}) : {};
  const results = gameState ? (gameState.results || []) : [];
  const lastResult = results.length > 0 ? results[results.length - 1] : null;

  const mySid = user && user.socketId;
  const myResponse = responses[mySid];
  const respondedCount = Object.keys(responses).length;

  // Reset local answer when a new round starts
  useEffect(() => {
    if (phase === 'responding') setMyAnswer(null);
  }, [phase, round]);

  const dispatch = async (action) => {
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  };

  const respond = (answer) => {
    if (myAnswer || myResponse) return;
    setMyAnswer(answer);
    dispatch({ type: 'respond', answer });
  };

  return (
    <div className="never-have-i-ever-game flex flex-col items-center min-h-full bg-gray-50 dark:bg-gray-950 p-2">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="text-sm text-gray-500 dark:text-gray-400">Round {round} of {rounds}</div>
          <div className="text-xs font-semibold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider">Never Have I Ever</div>
        </div>

        {/* ── RESPONDING phase ── */}
        {phase === 'responding' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
            {/* Statement card */}
            <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950 border-2 border-indigo-300 dark:border-indigo-600 p-4 mb-4 text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-indigo-500 dark:text-indigo-400 mb-2">🙈 Never have I ever…</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                {currentStatement
                  ? currentStatement.replace(/^never have i ever\s*/i, '')
                  : '…'}
              </div>
            </div>

            <TimerBar
              startTime={gameState && gameState.startTime}
              timeLimit={gameState && gameState.timeLimit}
              active={phase === 'responding'}
              color="bg-indigo-400"
            />

            {myAnswer || myResponse ? (
              <div className="mt-4 text-center">
                <div className={`inline-block px-5 py-3 rounded-xl text-lg font-bold border-2 ${
                  (myAnswer || myResponse) === 'have'
                    ? 'bg-rose-100 dark:bg-rose-900 border-rose-400 text-rose-700 dark:text-rose-300'
                    : 'bg-green-100 dark:bg-green-900 border-green-400 text-green-700 dark:text-green-300'
                }`}>
                  {(myAnswer || myResponse) === 'have' ? '🙋 I have!' : '🙅 I haven\'t'}
                </div>
                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  Waiting for others… <span className="font-semibold text-gray-700 dark:text-gray-200">{respondedCount}/{players.length}</span> answered
                </div>
              </div>
            ) : (
              <div className="flex gap-4 mt-4">
                <button
                  onClick={() => respond('have')}
                  className="flex-1 py-6 text-xl font-bold rounded-xl bg-rose-500 hover:bg-rose-600 active:bg-rose-700 active:scale-95 text-white shadow transition-all"
                >
                  🙋 I Have!
                </button>
                <button
                  onClick={() => respond('havent')}
                  className="flex-1 py-6 text-xl font-bold rounded-xl bg-green-500 hover:bg-green-600 active:bg-green-700 active:scale-95 text-white shadow transition-all"
                >
                  🙅 I Haven't
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── REVEAL phase ── */}
        {phase === 'reveal' && lastResult && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
            {/* Statement */}
            <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950 border-2 border-indigo-300 dark:border-indigo-600 p-3 mb-4 text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-indigo-500 dark:text-indigo-400 mb-1">🙈 Never have I ever…</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {(lastResult.statement || '').replace(/^never have i ever\s*/i, '')}
              </div>
            </div>

            {/* Summary counts */}
            <div className="flex gap-3 mb-4 justify-center">
              <div className="flex-1 text-center py-3 rounded-lg bg-rose-50 dark:bg-rose-950 border border-rose-300 dark:border-rose-700">
                <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">{lastResult.haveCount}</div>
                <div className="text-xs text-rose-600 dark:text-rose-400 font-semibold">I Have 🙋</div>
              </div>
              <div className="flex-1 text-center py-3 rounded-lg bg-green-50 dark:bg-green-950 border border-green-300 dark:border-green-700">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{lastResult.haventCount}</div>
                <div className="text-xs text-green-600 dark:text-green-400 font-semibold">I Haven't 🙅</div>
              </div>
            </div>

            {/* Per-player breakdown */}
            <div className="space-y-1.5 mb-3">
              {(lastResult.perPlayer || []).map(p => {
                const isMe = p.socketId === mySid;
                const didHave = p.response === 'have';
                return (
                  <div
                    key={p.socketId}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                      didHave
                        ? 'bg-rose-50 dark:bg-rose-950 border-rose-300 dark:border-rose-700'
                        : 'bg-green-50 dark:bg-green-950 border-green-300 dark:border-green-700'
                    }`}
                  >
                    <span className={`text-sm font-medium ${isMe ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-800 dark:text-gray-200'}`}>
                      {p.username || p.socketId}{isMe ? ' (you)' : ''}
                      {!p.answered && <span className="ml-1 text-xs text-gray-400">(no answer)</span>}
                    </span>
                    <span className={`text-base ${didHave ? 'text-rose-600 dark:text-rose-400' : 'text-green-600 dark:text-green-400'}`}>
                      {didHave ? '🙋 I Have!' : '🙅 I Haven\'t'}
                      {didHave && <span className="ml-1 text-xs font-bold">+1</span>}
                    </span>
                  </div>
                );
              })}
            </div>

            <TimerBar
              startTime={gameState && gameState.startTime}
              timeLimit={6}
              active={true}
              color="bg-gray-400"
            />
            <div className="text-center text-xs text-gray-400 dark:text-gray-500 mt-1">Next statement coming up…</div>
          </div>
        )}

        {/* ── FINISHED phase ── */}
        {phase === 'finished' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
            <div className="text-center text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">🏆 Game Over!</div>
            {gameState && gameState.result && gameState.result.winnerNames && (
              <div className="text-center text-indigo-600 dark:text-indigo-400 font-semibold mb-4">
                {gameState.result.outcome === 'tie' ? "It's a tie — " : 'Most experienced: '}
                {gameState.result.winnerNames}
              </div>
            )}
            <div className="space-y-2">
              {players
                .slice()
                .sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0))
                .map((p, i) => (
                  <div
                    key={p.socketId}
                    className={`flex items-center justify-between px-3 py-2 rounded border ${
                      p.socketId === mySid
                        ? 'bg-indigo-50 dark:bg-indigo-950 border-indigo-300'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-400 w-5">#{i + 1}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{p.username || p.socketId}</span>
                      {p.socketId === mySid && <span className="text-xs text-indigo-500">(you)</span>}
                    </div>
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                      {scores[p.socketId] || 0} <span className="text-xs text-gray-500 font-normal">I have's</span>
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* ── Scoreboard (during play) ── */}
        {phase !== 'finished' && players.length > 0 && (
          <div className="mt-4 bg-white dark:bg-gray-900 rounded-xl shadow p-3">
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Scores ("I Have" count)</div>
            <div className="flex flex-wrap gap-2">
              {players
                .slice()
                .sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0))
                .map((p) => (
                  <div
                    key={p.socketId}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm border ${
                      p.socketId === mySid
                        ? 'bg-indigo-50 dark:bg-indigo-950 border-indigo-300 font-semibold'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <span className={p.socketId === mySid ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-gray-300'}>
                      {p.username || p.socketId}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{scores[p.socketId] || 0}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

NeverHaveIEverGame.Guide = function NeverHaveIEverGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2 text-gray-900 dark:text-gray-100">Never Have I Ever</h3>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-300">
        <li>Each round shows a <strong>"Never have I ever…"</strong> statement.</li>
        <li>Secretly tap <strong>🙋 I Have!</strong> or <strong>🙅 I Haven't</strong> before time runs out.</li>
        <li>All answers are revealed at the same time — find out who's done what!</li>
        <li>You earn <strong>1 point</strong> each time you answer "I Have".</li>
        <li>The player with the most points wins — they've lived the most interesting life!</li>
      </ul>
    </div>
  );
};
